<template>
  <page-outline>
    <template slot="pageContent">
      <div :class="['content bg-light', { expanded: !isSidebarOpen }]">
        <div>

          <!-- TITLE -->
          <b-container fluid>
            <div class="content-title">
              <b-row>
                <h1>Documentation & Help</h1>
              </b-row>
              <b-row>
                <span class="content-subtitle">
                  Complete guide to using the Digital Credit Transfer Sandbox
                </span>
              </b-row>
            </div>
          </b-container>

          <!-- NAV -->
          <b-row class="nav-container">
            <b-col :class="{ active: showPanel === 'overview' }" @click="showPanel = 'overview'">Overview</b-col>
            <b-col :class="{ active: showPanel === 'features' }" @click="showPanel = 'features'">Features</b-col>
            <b-col :class="{ active: showPanel === 'workflows' }" @click="showPanel = 'workflows'">Workflows</b-col>
            <b-col :class="{ active: showPanel === 'glossary' }" @click="showPanel = 'glossary'">Glossary</b-col>
            <b-col :class="{ active: showPanel === 'faq' }" @click="showPanel = 'faq'">FAQ</b-col>
          </b-row>

          <!-- FEATURES -->
          <div v-if="showPanel === 'features'">
            <div class="card p-6" v-for="(feature, index) in features" :key="index">
              <div class="flex-between">
                <span class="text-title">{{ feature.title }}</span>
                <button class="btn btn-outline-light" @click="navigate('/' + feature.link)">
                  Open <i class="fa-light fa-arrow-right"></i>
                </button>
              </div>

              <span class="text-subtle">{{ feature.body }}</span>

              <div class="flex-between my-3-1 clickable" @click="toggleDetails(feature.title)">
                <span class="text-bold">View Details</span>
                <i :class="['fa-light fa-duotone', seeDetails === feature.title ? 'fa-angle-up' : 'fa-angle-down']"></i>
              </div>

              <div v-if="seeDetails === feature.title">
                <span class="text-bold">Quick Tips</span>
                <ul>
                  <li v-for="(detail, i) in feature.details" :key="i">{{ detail }}</li>
                </ul>

                <span class="text-bold">Key Points</span>
                <div v-for="(point, i) in feature.points" :key="i">
                  <span class="text-bold my-05 block">{{ point }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- WORKFLOWS -->
          <div v-if="showPanel === 'workflows'">
            <div class="card p-6" v-for="(workflow, index) in workflows" :key="index">
              <span class="workflow-title">
                <i class="fal fa-bolt icon-md icon-purple flex-center icon-rounded"></i>
                {{ workflow.title }}
              </span>

              <div class="workflow-item" v-for="(item, i) in workflow.items" :key="i">
                <span class="workflow-step">{{ i + 1 }}</span>
                <span class="text-subtle">{{ item }}</span>
              </div>
            </div>
          </div>

          <!-- GLOSSARY -->
          <div v-if="showPanel === 'glossary'" class="card p-6 glossary-grid">
            <div class="grid" v-for="(t, index) in glossary" :key="index">
              <span class="text-bold text-dark">{{ t.title }}</span>
              <span class="text-subtle">{{ t.body }}</span>
            </div>
          </div>

          <!-- FAQ -->
          <div v-if="showPanel === 'faq'">
            <div class="card p-6 faq-card" v-for="(faq, index) in faqs" :key="index">
              <div class="faq-header clickable" @click="toggleDetails(faq.title)">
                <div class="faq-title">
                  <i class="fa-regular fa-circle-question text-primary"></i>
                  <span class="text-lg">{{ faq.title }}</span>
                </div>
                <i :class="['fa-light fa-duotone', seeDetails === faq.title ? 'fa-angle-up' : 'fa-angle-down']"></i>
              </div>

              <span v-if="seeDetails === faq.title" class="faq-body">
                {{ faq.body }}
              </span>
            </div>
          </div>

          <!-- OVERVIEW -->
          <div v-if="showPanel === 'overview'">

            <div class="card p-6 my-2">
              <div class="flex-start flex-gap-1">
                <i class="fa-light fa-home icon-lg icon-blue flex-center icon-rounded"></i>
                <div class="grid">
                  <span class="text-lg text-dark">
                    Welcome to the Digital Credit Transfer Sandbox
                  </span>
                  <span class="text-subtle">
                    This sandbox environment allows you to test and understand the complete lifecycle of order creation,
                    payment processing, and automated reconciliation.
                  </span>
                </div>
              </div>
            </div>

            <span class="section-title">Core Concepts</span>

            <div class="grid-2 grid-gap-2 my-1">
              <div class="card p-6 card-shadow" v-for="(concept, i) in concepts" :key="i">
                <div class="flex-start flex-gap-1">
                  <div>
                    <i style="height: 2.5rem; width: 2.5rem; border-radius: 0.5rem; font-size: 1.1rem;" :style="concept.style" :class="[concept.icon, 'flex-center']"></i>
                  </div>
                  <div class="grid">
                    <span class="text-lg">{{ concept.title }}</span>
                    <span class="text-subtle">{{ concept.body }}</span>
                  </div>
                </div>
              </div>
            </div>

            <span class="section-title">Getting Started by Role</span>

            <div class="grid-3 grid-gap-2 my-1">
              <div style="gap: 2rem;" class="card p-6 role-card grid" v-for="(role, i) in roles" :key="i">
                <span class='btn-empty' :style='role.badge'>{{ role.name }}</span>
                <span>{{ role.subtitle }}</span>
                <ul>
                  <li v-for="(item, j) in role.items" :key="j">{{ item }}</li>
                </ul>
                <button class="btn btn-outline-light" @click="navigate(role.link)">
                  {{ role.cta }} <i class="fa-light fa-arrow-right"></i>
                </button>
              </div>
            </div>

            <div class="info-container p-6">
              <div class="flex-start flex-gap-1" style="color: #000;">
                <div>
                  <i class="fa-light fa-circle-exclamation icon-lg icon-primary flex-center icon-rounded"></i>
                </div>
                <div class="grid-gap-05 grid">
                  <span class="text-xl">Critical Business Rules</span>
                  <span class="rule" v-for="(rule, i) in rules" :key="i">
                    <i class="fa-light fa-circle-check text-success"></i>
                    <span class="text-bold">{{ rule.bold }}</span>{{ rule.text }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import { loadPage } from '@/helpers/routing'

export default {
  name: 'DocumentationPage',
  components: {
    'page-outline': PageOutline
  },
  data () {
    return {
      seeDetails: '',
      isSidebarOpen: true,
      showPanel: 'overview',
      workflows: [
        {
          title: 'Complete Order-to-Payment Flow',
          items: ['Customer creates order → System allocates VIBAN to client number', 'Customer receives order confirmation with VIBAN', 'Customer makes payment to VIBAN',  'Payment received → Automatch runs', 'System matches payment to order via VIBAN + amount + date', 'Reconciliation created → Order status: Payment Confirmed', 'Transaction appears in accounting ledger and reports']
        },
        {
          title: 'Manual Reconciliation (Auto-match Failed)',
          items: ['Check Unreconciled Payments in Reconciliation Ledger', "Identify payment that should match but didn't auto-match", 'Click "Reconcile" button', 'Select matching order(s) and payment(s)', 'Review amounts and add operator comment', 'Confirm reconciliation', 'Verify order status updates to Payment Confirmed']
        },
        {
          title: 'New Client Setup',
          items: ['Customer enters client number (or auto-generated)', 'System checks if VIBAN already allocated to this client number', 'If new client: allocate available VIBAN from pool', 'If existing client: reuse existing VIBAN', 'Client number + VIBAN link established', 'All future orders for this client use same VIBAN']
        },
        {
          title: 'Daily Reconciliation Reporting',
          items: ['Navigate to Daily Report page', 'Verify date set to today (or select specific date)', 'Review summary metrics: total reconciled, automation rate, manual count', 'Scroll through detailed reconciliation table', 'Click "Generate Daily Report" to export CSV', 'Save CSV for compliance/audit records', 'Submit to stakeholders if required']
        },
        {
          title: 'Troubleshooting Failed Auto-match',
          items: ['Check VIBAN matches between order and payment', 'Verify payment amount within tolerance (default ±0.5% or 0.5€)', 'Check payment date within date window', 'Review remittance info for clues', 'Check if order already reconciled (duplicate payment)', 'Verify automatch is running (not paused)', 'If all checks pass, use manual reconciliation']
        },
        {
          title: 'Unreconcile a Transaction',
          items: ['Navigate to Transaction Ledger page', 'Find the reconciled transaction (filter by Status: Reconciled)', 'Note the Reconciliation Reference (e.g., REC-000001)', 'Click "Unreconcile" button in the Actions column', 'Review dialog showing all linked orders and payments', 'Verify the amounts and check the warning message', 'Click "Confirm Unreconcile" to proceed', 'System will:', 'Mark reconciliation as Cancelled', 'Return all orders to Pending status', 'Return all payments to Pending status', 'Remove outstanding balances and write-offs', 'Create reversal accounting entries', 'Log the action in audit trail', 'Verify transactions now show "Pending" status', 'Transactions can now be re-reconciled (auto or manual)']
        }
      ],
      glossary: [
        {
          title: 'VIBAN',
          body: 'Virtual IBAN - A unique virtual account number used to route payments to specific clients. One VIBAN per client number (not per order).'
        },
        {
          title: 'Client Number',
          body: 'Your unique customer identifier. Required for all customers. VIBANs are allocated to client numbers, and all orders from that client use the same VIBAN.'
        },
        {
          title: 'Order Reference',
          body: 'Optional identifier for a specific order. Used in payment remittance to help identify which order a payment is for when amounts are identical.'
        },
        {
          title: 'Reconciliation',
          body: 'The process of matching orders with payments. Can be automatic (based on rules) or manual (operator-driven).'
        },
        {
          title: 'Tolerance',
          body: 'Acceptable variation between order and payment amounts. Can be percentage-based (±0.5%) or absolute (±0.5€). Allows auto-matching with small differences.'
        },
        {
          title: 'Automatch',
          body: 'Automated reconciliation engine that continuously checks for matching orders/payments based on VIBAN, amount tolerance, and date windows.'
        },
        {
          title: 'Date Window',
          body: 'Acceptable time range (days before/after due date) for payment auto-matching. Prevents late payments from matching automatically.'
        },
        {
          title: 'Grace Period',
          body: 'Extension to the date window allowing slightly late payments to still auto-match. Handles reasonable delays.'
        },
        {
          title: 'Overpayment',
          body: 'When payment amount exceeds order amount. Can be handled as refund, credit, or manual decision based on configuration.'
        },
        {
          title: 'Unreconciliation',
          body: 'The process of reversing a reconciliation. Cancels the match, returns orders and payments to Pending status, removes outstanding balances and write-offs, and creates reversal accounting entries.'
        },
        {
          title: 'Reconciliation Reference',
          body: 'Unique identifier (e.g., REC-000001) assigned to each reconciliation. Links all related transactions and appears in the Transaction Ledger for easy tracking.'
        },
        {
          title: 'Remittance Info',
          body: 'Reference text included with payment. Often contains order reference to help identify which order the payment is for.'
        },
        {
          title: 'Dereconciliation',
          body: 'Undoing a reconciliation. Returns order and payment to unmatched state, requiring new reconciliation.'
        },
        {
          title: 'Junction Table',
          body: 'Database table linking reconciliations with orders/payments. Allows many-to-many relationships (one reconciliation can involve multiple orders/payments).'
        },
        {
          title: 'Allocation',
          body: "Assigning a VIBAN to a client number. Makes the VIBAN active and ready to receive payments for that client's orders."
        },
        {
          title: 'Automation Rate',
          body: 'Percentage of reconciliations completed automatically vs manually. Key performance indicator - target >95%.'
        }
      ],
      features: [
        {
          title: 'Dashboard Overview',
          body: 'Your central hub for monitoring reconciliation operations, order status, and system health.',
          link: 'dashboard',
          details: ['Dashboard shows real-time metrics updated automatically', 'Click on any metric card to drill down into details', 'Overdue orders require immediate attention', 'Automation rate target: >95% for optimal efficiency'],
          points: ['Overdue Orders: Orders past due date without payment confirmation', 'Not Yet Due: Active orders still within payment window', "Today's Volume: Total EUR reconciled today", 'Automation Rate: % of auto-matched reconciliations']
        },
        {
          title: 'VIBAN Management',
          body: 'Manage Virtual IBANs - unique payment identifiers allocated to client numbers for payment tracking.',
          link: 'vibanmanagement',
          details: ['VIBANs are ONLY allocated to client numbers (not individual orders)', 'One client can have multiple VIBANs if needed', 'Status lifecycle: Available → Allocated → (optional) Deactivated', 'Use bulk import for large VIBAN pools', 'Never delete VIBANs with transaction history'],
          points: ['VIBAN = Virtual IBAN, used for payment routing:', 'Client Number = Your internal customer ID:', 'Available VIBANs are ready for allocation:', 'Allocated VIBANs are in active use:', 'One VIBAN serves ALL orders for that client:']
        },
        {
          title: 'Reconciliation Ledger',
          body: 'Complete reconciliation hub showing matched orders/payments, unmatched items, and reconciliation history.',
          link: 'reconciliationledger',
          details: ['Reconciled tab: Successfully matched transactions', 'Unreconciled Orders: Awaiting payment', 'Unreconciled Payments: Need manual matching', 'Use filters to find specific transactions quickly', 'Export data for reporting and compliance'],
          points: ['AUTO matches are done by the system automatically:', 'MANUAL matches require operator intervention:', 'Tolerance allows small amount variations:', 'Date windows prevent late payment matches:', 'All reconciliations are audited:']
        },
        {
          title: 'Transaction Ledger',
          body: 'Unified view of all financial events: orders, payments, reconciliations, and refunds. The Transaction Ledger is the primary interface for unreconciliation operations.',
          link: 'transactionledger',
          details: ['Filter by transaction type to focus on specific events', 'Use date range for period analysis', 'Search by reference numbers or customer names', 'Export filtered data for reporting', 'Click "Unreconcile" button on reconciled transactions to reverse them', 'Reconciliation Reference (Recon Ref) links all related transactions'],
          points: ['Shows chronological flow of all transactions:', 'Links related events together via Reconciliation Reference:', 'Supports compliance and audit requirements:', 'Real-time updates as transactions occur:', 'Unreconciliation reverses matches and creates audit trail:', 'Only reconciled transactions can be unreconciled:']
        },
        {
          title: 'Daily Reconciliation Report',
          body: 'Generate daily reconciliation reports for compliance, audit, and performance monitoring.',
          link: 'dailyreport',
          details: ["Defaults to today's date automatically", 'Summary cards show key daily metrics', 'Export full report for audit trail', 'Select specific rows for partial exports', 'Review manual reconciliations for quality control'],
          points: ["Audit-ready format for compliance:", "Shows automated vs manual split:", "Includes operator names for accountability:", "Complete transaction details included:"]
        },
        {
          title: 'Configuration & Allocation Rules',
          body: 'Configure system behavior: tolerance levels, date windows, overpayment handling, and automatch settings.',
          link: 'configuration',
          details: ["Tolerance allows payment flexibility (default ±0.5% or 0.5€)", "Date windows prevent late payment auto-match", "Overpayment rules determine credit/refund behavior", "Automatch runs continuously when enabled", "Test changes with small amounts first"],
          points: ["Higher tolerance = more auto-matches but less precision:", "Lower tolerance = fewer auto-matches but more accuracy:", "Date windows prevent stale payment matching:", "Grace period allows reasonable delays:", "Changes take effect immediately:"]
        },
        {
          title: 'User Management',
          body: 'Manage users and their role-based permissions: Admin, Consultant, and Reconciliation Specialist.',
          link: 'usermanagement',
          details: ["Admin: Full access to all features", "Consultant: Read-only access for monitoring", "Reconciliation Specialist: Reconciliation operations only", "Each role sees different navigation items", "Changes take effect immediately"],
          points: ["Role-based access control (RBAC):", "Consultants cannot modify data:", "All user actions are audited:", "Email must be unique:"]
        },
        {
          title: 'Audit Logs',
          body: 'Complete audit trail of all system actions for compliance, security, and troubleshooting.',
          link: 'auditlogs',
          details: ["Every action is logged with timestamp and user", "Filter by action type to focus investigation", "Search by reference numbers or user names", "Export logs for compliance reporting", "Critical events are highlighted"],
          points: ["Immutable audit trail:", "Supports compliance requirements:", "Helps troubleshoot issues:", "Shows complete system history:"]
        },
        {
          title: 'Create Order',
          body: 'Customer interface for creating new orders with VIBAN assignment.',
          link: '',
          details: ["Client number is REQUIRED (auto-generated if empty)", "VIBAN is automatically allocated to your client number", "Order reference is optional but recommended", "Due date determines payment deadline", "All orders are tracked in real-time"],
          points: ["Client number links all your orders:", "VIBAN is used for payment routing:", "Status updates automatically when paid:", "Use same client number for repeat customers:"]
        },
        {
          title: 'Payment Simulation',
          body: 'Simulate customer payments for testing reconciliation workflows.',
          link: '',
          details: ["Enter the VIBAN from your order", "Use exact or slightly different amount to test tolerance", "Remittance info helps with manual matching", "Automatch runs automatically after payment", "Check Order Status to verify reconciliation"],
          points: ["Simulates real payment received:", "Triggers automatch process:", "Tests your reconciliation rules:", "Safe sandbox environment:"]
        },
        {
          title: 'Order Status',
          body: 'Track your orders and view payment/reconciliation status.',
          link: '',
          details: ["Pending Allocation: Waiting for VIBAN", "Awaiting Payment: Order created, payment needed", "Payment Confirmed: Successfully reconciled", "Use filters to find specific orders", "Click order to see detailed breakdown"],
          points: ["Real-time status updates:", "Shows complete order lifecycle:", "Links to related payments:", "Displays VIBAN for payment:"]
        }
      ],
      faqs: [
        {
          title: 'Why are VIBANs allocated to client numbers instead of order references?',
          body: 'This is a critical business rule. VIBANs must be allocated to client numbers (not orders) because: (1) One client may have multiple orders, (2) Customers pay from the same account for all their orders, (3) VIBAN acts as a permanent payment route for that client, (4) Simplifies payment reconciliation across multiple orders, (5) Order references are optional and used only for disambiguation in remittance info.'
        },
        {
          title: "What happens if a payment amount doesn't exactly match the order?",
          body: 'The system uses tolerance settings. Default is ±0.5% relative tolerance or 0.5€ absolute tolerance. If payment is within tolerance, automatch succeeds. If outside tolerance, manual reconciliation is required. You can adjust tolerance in Configuration.'
        },
        {
          title: "Why didn't my payment auto-match?",
          body: 'Common reasons: (1) VIBAN mismatch, (2) Amount outside tolerance, (3) Payment date outside date window, (4) Order already reconciled, (5) Automatch paused. Check Unreconciled Payments tab and verify all matching criteria.'
        },
        {
          title: 'Can I delete a VIBAN?',
          body: 'Never delete VIBANs with transaction history - this breaks audit trails. Instead, deallocate the VIBAN (returns to Available status) or deactivate it. Deactivated VIBANs are preserved for historical records but not allocated to new clients.'
        },
        {
          title: "What's the difference between AUTO and MANUAL reconciliation?",
          body: 'AUTO: System automatically matched based on VIBAN, amount tolerance, and date window. No operator intervention. MANUAL: Operator manually linked orders/payments, typically because auto-match criteria not met. Manual reconciliations require operator comment for audit.'
        },
        {
          title: 'How do I handle overpayments?',
          body: 'Overpayment behavior is configurable: (1) Refund: Excess amount refunded to customer, (2) Credit: Held as credit for future orders, (3) Manual: Requires operator decision. Set in Configuration → Overpayment Behavior.'
        },
        {
          title: 'What is the automation rate and why does it matter?',
          body: 'Automation rate = (auto-matches / total reconciliations) × 100%. Target is >95%. Higher rate means less manual work, faster processing, fewer errors, and lower operational costs. Low rate indicates matching rules need adjustment.'
        },
        {
          title: 'Can one reconciliation involve multiple orders or payments?',
          body: 'Yes! This is called partial reconciliation. Examples: (1) One payment covering multiple orders, (2) Multiple payments for one large order. The system uses junction tables to link many orders to many payments in a single reconciliation.'
        },
        {
          title: 'Why is client number auto-generated if I leave it empty?',
          body: "Client numbers are REQUIRED for VIBAN allocation. If customer doesn't provide one, system auto-generates a unique ID to ensure every order has a valid client number for VIBAN assignment. You can update it later if needed."
        },
        {
          title: 'How do I export data for compliance reporting?',
          body: 'Every table has export buttons: (1) Export Selected: Only checked rows, (2) Export Filtered: All visible rows after filters, (3) Export All: Complete dataset. Daily Report page has "Generate Daily Report" for audit-ready daily exports. All exports are CSV format.'
        },
        {
          title: 'When should I unreconcile a transaction?',
          body: 'Unreconcile when: (1) Wrong order matched to payment (operator error), (2) Duplicate reconciliation detected, (3) Payment was incorrect and needs refund, (4) Order details changed after reconciliation, (5) Testing or training purposes. Unreconciling creates full audit trail and reversal accounting entries. The transactions return to Pending and can be re-matched.'
        },
        {
          title: 'What happens to accounting entries when I unreconcile?',
          body: 'The system creates reversal accounting events to maintain double-entry bookkeeping integrity. Original reconciliation is marked as "Cancelled" (never deleted), all linked orders and payments return to Pending status, outstanding balances and write-offs are removed, and a complete audit log is created. The Reconciliation Reference remains for historical tracking.'
        },
        {
          title: 'Can I unreconcile a transaction from several days ago?',
          body: "Yes! There are no time restrictions on unreconciliation. You can reverse any reconciliation regardless of when it was created. The system will properly handle all reversals, accounting entries, and status updates. However, always check with your accounting team before unreconciling old transactions as it may affect closed reporting periods."
        }
      ],
      concepts: [
        {
          title: 'VIBAN-First Architecture',
          body: 'VIBANs are allocated to client numbers (not orders). One client = one VIBAN for all their orders. This enables automatic payment routing and reconciliation.',
          icon: 'fa-light fa-file',
          style: 'background-color: #ffedd4; color: #f54a00;',
        },
        {
          title: 'Automated Reconciliation',
          body: 'Automatch engine continuously matches orders with payments using VIBAN, amount tolerance, and date windows. Target automation rate: > 95%.',
          icon: 'fa-light fa-circle-check',
          style: 'background-color: #dcfce7; color: #00a63e;'
        },
        {
          title: 'Client Number Required',
          body: 'Every customer must have a client number. If not provided, system auto-generates one. Client numbers link all orders from the same customer.',
          icon: 'fa-light fa-user-group',
          style: 'background-color: #f3e8ff; color: #9810fa;'
        },
        {
          title: 'Tolerance-Based Matching',
          body: 'Payments within ±0.5% (default) of order amount auto-match. Handles rounding differences, fees, and small variations without manual intervention.',
          icon: 'fa-light fa-bolt-lightning',
          style: 'background-color: #dbeafe; color: #155dfc;'
        }
      ],
      roles: [
        {
          badge: 'background-color: #e7000b; color: #fff; width: fit-content; border: none;',
          name: 'Admin',
          subtitle: 'Full System Access',
          items: ['Manage VIBAN pool','Configure matching rules','Perform reconciliations','Manage users','View all reports'],
          link: '/vibanmanagement',
          cta: 'Start with VIBAN Setup'
        },
        {
          badge: 'background-color: #155dfc; color: #fff; width: fit-content; border: none;',
          name: 'Reconciliation Specialist',
          subtitle: 'Operations Focus',
          items: ['View reconciliation ledgers','Perform manual matching','Generate daily reports','Monitor automation rate','Review audit logs'],
          link: '/reconciliationledger',
          cta: 'Go to Reconciliation'
        },
        {
          badge: 'background-color: #00a63e; color: #fff; width: fit-content; border: none;',
          name: 'Consultant',
          subtitle: 'Read-Only Monitoring',
          items: [ 'View all data', 'Generate reports', 'Export data', 'Monitor performance', 'No modification rights'],
          link: '/dashboard',
          cta: 'View Dashboard'
        }
      ],
      rules: [
        {
          bold: 'VIBANs are ONLY allocated to client numbers -',
          text: 'never to order references'
        },
        {
          bold: 'Client numbers are REQUIRED -',
          text: 'auto-generated if not provided'
        },
        {
          bold: 'Order references are optional -',
          text: 'used only in remittance info for disambiguation'
        },
        {
          bold: 'One VIBAN per client -',
          text: 'serves all orders for that client'
        }
      ]
    }
  },
  methods: {
    navigate(url){
      loadPage(this, {url: url})
    },
    toggleDetails(detail){
      if(this.seeDetails == detail){
        this.seeDetails = ''
      } else{
        this.seeDetails = detail
      }
    }
  }
}
</script>

<style scoped>

.info-container{
  display: flex;
  align-items: baseline;
  gap: 1rem;
  padding: calc(.25rem * 4);
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: .625rem;
  margin: 1rem 0rem;
}

.content-title{
  margin: 1.5rem 0rem;
}

.content-title>div>span{
  color: #000000a6;
  font-size: 1.1rem;
}

ul{
  padding-left: 1rem;
}

/* ---------- layout ---------- */
.flex { display: flex; }
.flex-center { display: flex; align-items: center; justify-content: center; }
.flex-between { display: flex; align-items: center; justify-content: space-between; }
.flex-start { display: flex; align-items: flex-start; }
.flex-gap-1 { gap: 1rem; }

.grid { display: grid; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; }
.grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; }
.grid-gap-2 { gap: 0rem 2rem; }
.grid-gap-05 { gap: 0.5rem; }

/* ---------- typography ---------- */
.text-title { font-size: 1.5rem; color: #101828; }
.text-lg { font-size: 1.1rem; }
.text-xl { font-size: 1.3rem; }
.text-bold { font-weight: bold; }
.text-subtle { color: #4a5565; }
.text-dark { color: #101828; }
.text-primary { color: #155dfc; }
.text-success { color: #00a63e; }

/* ---------- spacing ---------- */
.my-05 { margin: 0.5rem 0; }
.my-1 { margin: 1rem 0; }
.my-2 { margin: 2rem 0; }
.my-3-1 { margin: 3rem 0 1rem; }

/* ---------- icons ---------- */
.icon-lg { width: 4rem; height: 4rem; font-size: 1.2rem; }
.icon-md { width: 2.5rem; height: 2.5rem; font-size: 1.1rem; }
.icon-rounded { border-radius: 0.5rem; }

.icon-blue { background: #dbeafe; color: #155dfc; }
.icon-purple { background: #f3e8ff; color: #9810fa; }
.icon-primary { background: #155dfc; color: #fff; }

/* ---------- cards ---------- */
.card-shadow {
  box-shadow: 0 4px 6px -1px #0000001a, 0 2px 4px -2px #0000001a;
}

/* ---------- nav ---------- */
.nav-container {
  height: 2.5rem;
  padding: 0.2rem;
  margin: 2rem 0;
  background: #e7e7e7;
  border-radius: 1rem;
}

.nav-container > div {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  border: none;
  border-radius: calc(0.625rem + 4px);
  color: #000;
  font-weight: bold;
  cursor: pointer;
}

.nav-container > div.active {
  background: #fff;
  cursor: default;
}

/* ---------- workflow ---------- */
.workflow-title {
  display: flex;
  gap: 1rem;
  font-size: 1.5rem;
  color: #101828;
}

.workflow-item {
  display: flex;
  gap: 1rem;
  padding-left: 4rem;
  margin: 0.5rem 0;
  align-items: center;
}

.workflow-step {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: #dbeafe;
  color: #155dfc;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* ---------- glossary ---------- */
.glossary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

/* ---------- faq ---------- */
.faq-card { min-height: 4rem; }
.faq-header { display: flex; justify-content: space-between; align-items: center; }
.faq-title { display: flex; gap: 1rem; align-items: center; font-weight: bold; }
.faq-body { margin-top: 1rem; color: #4a5565; }

/* ---------- misc ---------- */
.clickable { cursor: pointer; }
.section-title {
  font-size: 1.3rem;
  color: #101828;
  margin: 2rem 0;
}

</style>